import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faStar as farStar } from '@fortawesome/free-regular-svg-icons';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { SignalFieldBaseDirective } from '../signal-field-base.directive';

/** stars shown when neither the schema nor the caller says otherwise */
const defaultMaxRating = 5;

/**
 * Low-level star rating for signal forms.
 *
 * The value is the number of stars — `number | null`, empty when nothing has been rated —
 * which also decides how many stars are drawn: the ceiling comes from the schema's
 * `max()` rule through the `FormUiControl` contract, falling back to `maxRating` and then
 * to five. So `max(path.quality, 10)` gives ten stars and validates against ten, with no
 * second place to keep the number in step.
 *
 * ```html
 * <pace-core-rating-field [formField]="signalForm.quality" ariaLabel="Service quality" />
 * ```
 *
 * Two corrections to the reactive-forms `RatingInputComponent`:
 *
 * - the value is a number. `RatingModel.value` is typed `string` while holding a star
 *   count, so every consumer parses it back; and because its validity check is `!value`,
 *   a legitimate zero reads as "not rated".
 * - the stars are buttons rather than an `ngx-bootstrap` `<rating>` with a custom
 *   template, so each one carries its own label and the group needs no third-party
 *   dependency.
 *
 * Clicking the current rating does not clear it, matching the reactive-forms widget. A
 * rating is cleared by the form, not by the stars.
 */
@Component({
    selector: 'pace-rating-field',
    templateUrl: './rating-field.component.html',
    styleUrls: ['./rating-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FaIconComponent],
})
export class RatingFieldComponent extends SignalFieldBaseDirective<number | null> {
    /** the number of stars given; `null` while nothing has been rated */
    public readonly value = model<number | null>(null);

    // --- `FormUiControl` inputs, all bound automatically by `[formField]` -----

    public readonly min = input<number | undefined>(undefined);

    /** the schema's ceiling, which doubles as the number of stars to draw */
    public readonly max = input<number | undefined>(undefined);

    // --- own inputs ----------------------------------------------------------

    /** stars to draw when the schema carries no `max` rule */
    public readonly maxRating = input(defaultMaxRating);

    public readonly faStar = faStar;
    public readonly farStar = farStar;

    /** the ratings on offer, 1..max, so the template can iterate them directly */
    public readonly ratings = computed(() => {
        const highest = this.max() ?? this.maxRating();

        return Array.from({ length: Math.max(highest, 0) }, (_unused, index) => index + 1);
    });

    public isFilled(rating: number): boolean {
        return rating <= (this.value() ?? 0);
    }

    /**
     * @param rating the star that was clicked
     */
    public rate(rating: number): void {
        if (!this.editable()) {
            return;
        }

        this.value.set(rating);
        // a click is an interaction, even though the stars never blur the way an input does
        this.touched.set(true);
    }
}
